import { useState, useEffect, useRef, useCallback, memo } from 'react';
import {
  Box,
  Chip,
  IconButton,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import ImageIcon from '@mui/icons-material/Image';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import EmailIcon from '@mui/icons-material/Email';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import ShareIcon from '@mui/icons-material/Share';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import type { Item } from '../../types';
import { hasFlag, ItemFlags } from '../../types';

const typeIcons = {
  url: <LinkIcon fontSize="small" />,
  text: <TextFieldsIcon fontSize="small" />,
  image: <ImageIcon fontSize="small" />,
  file: <AttachFileIcon fontSize="small" />,
  email: <EmailIcon fontSize="small" />,
};

interface ItemListRowProps {
  item: Item;
  onClick: (item: Item) => void;
  onEdit?: (item: Item) => void;
  onDelete?: (item: Item) => void;
  onRefresh?: (item: Item) => void;
  onShare?: (item: Item) => void;
  onAddToFolder?: (item: Item) => void;
  onToggleStar?: (item: Item) => void;
  onTagClick?: (tag: string) => void;
}

export default memo(function ItemListRow({ item: itemProp, onClick, onEdit, onDelete, onRefresh, onShare, onAddToFolder, onToggleStar, onTagClick }: ItemListRowProps) {
  // Self-poll when enrichment is pending — avoids parent re-renders
  const [localItem, setLocalItem] = useState<Item | null>(null);
  const item = localItem ?? itemProp;

  useEffect(() => {
    setLocalItem(null);
  }, [itemProp]);

  const lastJsonRef = useRef(JSON.stringify({
    title: itemProp.title, summary: itemProp.summary, imageUrl: itemProp.imageUrl,
    enrichmentStatus: itemProp.enrichmentStatus, tags: itemProp.tags, autoTags: itemProp.autoTags,
  }));
  useEffect(() => {
    if (item.enrichmentStatus !== 'pending') return;
    const interval = setInterval(async () => {
      const { getItem } = await import('../../services/items');
      const updated = await getItem(item.id);
      if (updated) {
        const json = JSON.stringify({
          title: updated.title, summary: updated.summary, imageUrl: updated.imageUrl,
          enrichmentStatus: updated.enrichmentStatus, tags: updated.tags, autoTags: updated.autoTags,
        });
        if (json !== lastJsonRef.current) {
          lastJsonRef.current = json;
          setLocalItem(updated);
        }
        if (updated.enrichmentStatus !== 'pending') clearInterval(interval);
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [item.id, item.enrichmentStatus, itemProp]);

  const handleRefreshLocal = useCallback(async () => {
    if (!onRefresh) return;
    await onRefresh(item);
    setLocalItem({ ...item, enrichmentStatus: 'pending' as const });
  }, [onRefresh, item]);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const allTags = [...item.tags, ...item.autoTags];

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleShare = () => {
    handleMenuClose();
    onShare?.(item);
  };

  const handleRefresh = () => {
    handleMenuClose();
    handleRefreshLocal();
  };

  const handleEdit = () => {
    handleMenuClose();
    onEdit?.(item);
  };

  const handleAddToFolder = () => {
    handleMenuClose();
    onAddToFolder?.(item);
  };

  const handleDelete = () => {
    handleMenuClose();
    onDelete?.(item);
  };

  const displayTitle = item.title && !item.title.startsWith('http')
    ? item.title
    : item.site || item.title;

  const dateStr = new Date(item.createdAt).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });

  return (
    <ListItemButton
      onClick={() => onClick(item)}
      sx={{ border: 1, borderColor: 'divider', gap: 1 }}
    >
      {item.imageUrl ? (
        <Box
          component="img"
          src={item.imageUrl}
          alt=""
          sx={{ width: 40, height: 40, borderRadius: 1, objectFit: 'cover', flexShrink: 0 }}
        />
      ) : (
        <ListItemIcon sx={{ minWidth: 32 }}>
          {typeIcons[item.type]}
        </ListItemIcon>
      )}
      <ListItemText
        primary={displayTitle}
        secondary={item.summary || undefined}
        slotProps={{
          primary: { variant: 'body2', fontWeight: hasFlag(item.flags, ItemFlags.READ) ? 500 : 700, noWrap: true },
          secondary: { variant: 'caption', noWrap: true },
        }}
        sx={{ minWidth: 0 }}
      />
      {allTags.length > 0 && (
        <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 0.5, flexShrink: 0 }}>
          {allTags.slice(0, 2).map((tag) => (
            <Chip
              key={tag}
              label={tag}
              size="small"
              variant="outlined"
              onClick={onTagClick ? (e: React.MouseEvent) => { e.stopPropagation(); onTagClick(tag); } : undefined}
              clickable={!!onTagClick}
            />
          ))}
        </Box>
      )}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: { xs: 'none', md: 'block' }, flexShrink: 0, whiteSpace: 'nowrap' }}
      >
        {dateStr}
      </Typography>
      {onToggleStar && (
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); onToggleStar(item); }}
          sx={{ p: 0.25, flexShrink: 0, color: hasFlag(item.flags, ItemFlags.STARRED) ? 'warning.main' : 'action.disabled' }}
        >
          {hasFlag(item.flags, ItemFlags.STARRED) ? <StarIcon sx={{ fontSize: 16 }} /> : <StarBorderIcon sx={{ fontSize: 16 }} />}
        </IconButton>
      )}
      {(onShare || onEdit || onDelete || onRefresh || onAddToFolder) && (
        <IconButton
          size="small"
          onClick={handleMenuOpen}
          sx={{ flexShrink: 0 }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      )}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
      >
        {onShare && (
          <MenuItem onClick={handleShare}>
            <ListItemIcon><ShareIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Share</ListItemText>
          </MenuItem>
        )}
        {onRefresh && (
          <MenuItem onClick={handleRefresh}>
            <ListItemIcon><RefreshIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Refresh</ListItemText>
          </MenuItem>
        )}
        {onEdit && (
          <MenuItem onClick={handleEdit}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )}
        {onAddToFolder && (
          <MenuItem onClick={handleAddToFolder}>
            <ListItemIcon><PlaylistAddIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Add to Folder</ListItemText>
          </MenuItem>
        )}
        {onDelete && (
          <MenuItem onClick={handleDelete}>
            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </ListItemButton>
  );
})
